import { useEffect, useState } from 'react'
import { api } from '../services'
import { Spinner } from './Spinner'
import { AlertSettingsItem, AlertTypeEnums, ALERT_SETTINGS_GLOBAL_DEVICE_ID } from '../common/constants'
import { BASE_SETTINGS, getAlertSettingsFromBackend, ORDER_ITEMS } from '../services/alertSettings'
import { titleCase } from '../common/utils'

const OPTIONS = [
  { value: AlertTypeEnums.SENSOR_ALERTS },
  { value: AlertTypeEnums.SYSTEM_CONTROL_MESSAGES },
  { value: AlertTypeEnums.TEST_MESSAGES },
]

interface Props {
  advancedMode: boolean
}

export function UserSettingsAlertSettings({ advancedMode }: Props) {
  const [isLoading, setIsLoading] = useState(false)
  const [alertType, setAlertType] = useState<string>(AlertTypeEnums.SENSOR_ALERTS)
  const [settings, setSettings] = useState(BASE_SETTINGS)

  useEffect(() => {
    if (!advancedMode) {
      setAlertType(AlertTypeEnums.SENSOR_ALERTS)
    }
  }, [advancedMode])

  useEffect(() => {
    setIsLoading(true)
    getAlertSettingsFromBackend(ALERT_SETTINGS_GLOBAL_DEVICE_ID, alertType)
      .then((settings) => setSettings(settings))
      .catch((error) => console.error(error))
      .finally(() => setIsLoading(false))
    return () => {}
  }, [alertType])

  // TODO: do some regex validation (on format depending on contact type)
  function updateSettings(newSettings: AlertSettingsItem) {
    const updatedSettings = settings.map((setting) => {
      if (
        setting.alertType === newSettings.alertType &&
        setting.contactType === newSettings.contactType &&
        setting.itemOrder === newSettings.itemOrder
      ) {
        setting.enabled = newSettings.enabled
        setting.contact = newSettings.contact
        setting.message = newSettings.message
        setting.totalAttempts = newSettings.totalAttempts < 0 ? 0 : newSettings.totalAttempts
        setting.retryDelay = newSettings.retryDelay < 0 ? 0 : newSettings.retryDelay

        if (JSON.stringify(newSettings) !== JSON.stringify(settings)) {
          setting.changed = true
        }
      }

      return setting
    })

    setSettings(updatedSettings)
  }

  function updateSettingsOnBackend(settings: AlertSettingsItem, force = false) {
    if (settings.contact === '') {
      console.log('contact is empty -- do not call backend')
      return
    }

    if (!settings.changed && !force) {
      console.log('settings have not changed -- do not call backend')
      return
    }

    const method = settings.id === undefined ? api.createAlertSettings : api.updateAlertSettings

    setIsLoading(true)

    method(settings)
      .then((result) => {
        const setting = getSetting(settings)
        if (setting) {
          setting.changed = false
          if (result.id) {
            setting.id = result.id
          }
        }
      })
      .catch((error) => {
        console.error(error)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  function getSetting(item: any): AlertSettingsItem | undefined {
    return settings.find((setting) => setting.contactType === item.contactType && setting.itemOrder === item.itemOrder)
  }

  return (
    <div className="mb-10">
      <div className={tailwind.header}>
        Alert Settings
        <div className="pt-1">{isLoading && <Spinner size={20} color="white" />}</div>
      </div>
      {advancedMode ? (
        <div className="flex my-4 justify-end">
          <label>
            <select value={alertType} onChange={(e) => setAlertType(e.target.value)}>
              {OPTIONS.map((option) => (
                <option value={option.value}>{option.value}</option>
              ))}
            </select>
          </label>
        </div>
      ) : null}
      <table className={tailwind.table}>
        <thead>
          <tr className="h-0 m-0">
            <td className="w-[10px]"></td>
            <td className={tailwind.alertTypeLabel}>Item</td>
            <td className={tailwind.alertContactInput}>Contact</td>
            <td>Custom Message</td>
            <td className={tailwind.input}>Attempts</td>
            <td className={tailwind.input}>Delay</td>
          </tr>
        </thead>
        <tbody>
          {ORDER_ITEMS.map((item, index) => {
            const setting = getSetting(item)
            if (!setting) return null
            return (
              <tr key={index}>
                <td className="w-[10px]">
                  <input
                    className="mx-3"
                    type="checkbox"
                    checked={setting.enabled}
                    onChange={(e) => {
                      const data = {
                        ...setting,
                        enabled: e.target.checked,
                      }
                      updateSettings(data)
                      updateSettingsOnBackend(data, true)
                    }}
                  />
                </td>
                <td className={tailwind.alertTypeLabel}>{`${titleCase(setting.contactType)} ${setting.itemOrder}`}</td>
                <td className={tailwind.alertContactInput}>
                  <input
                    className={tailwind.inputText}
                    type="text"
                    id={`alert-email-${setting.itemOrder}`}
                    placeholder={`${titleCase(setting.contactType)} ${setting.itemOrder}`}
                    value={setting.contact}
                    onChange={(e) => {
                      updateSettings({
                        ...setting,
                        contact: e.target.value,
                      })
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        updateSettingsOnBackend(setting)
                      }
                    }}
                    onBlur={(e) => {
                      updateSettingsOnBackend(setting)
                    }}
                  />
                </td>
                <td>
                  <input
                    className={tailwind.inputText}
                    type="text"
                    id={`alert-message-email-${setting.itemOrder}`}
                    placeholder="Custom Message"
                    value={setting.message}
                    onChange={(e) => {
                      updateSettings({
                        ...setting,
                        message: e.target.value,
                      })
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        updateSettingsOnBackend(setting)
                      }
                    }}
                    onBlur={(e) => {
                      updateSettingsOnBackend(setting)
                    }}
                  />
                </td>
                <td className={tailwind.input}>
                  <input
                    className={tailwind.inputText}
                    type="number"
                    value={setting.totalAttempts < 0 ? '' : setting.totalAttempts}
                    placeholder="..."
                    onChange={(e) => {
                      updateSettings({
                        ...setting,
                        totalAttempts: Number(e.target.value),
                      })
                    }}
                    onBlur={(e) => {
                      updateSettingsOnBackend({
                        ...setting,
                        totalAttempts: Number(e.target.value),
                      })
                    }}
                  />
                </td>
                <td className={tailwind.input}>
                  <input
                    className={tailwind.inputText}
                    type="number"
                    value={setting.retryDelay < 0 ? '' : setting.retryDelay}
                    placeholder="..."
                    onChange={(e) => {
                      updateSettings({
                        ...setting,
                        retryDelay: Number(e.target.value),
                      })
                    }}
                    onBlur={(e) => {
                      updateSettingsOnBackend({
                        ...setting,
                        retryDelay: Number(e.target.value),
                      })
                    }}
                  />
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

const tailwind = {
  header: 'flex flex-auto justify-between text-black text-l font-regular bg-[#BBB] py-2.5 pl-4 pr-4 w-full mb-4',
  table: 'w-full mx-3',
  inputText:
    'appearance-none border border-red-500 rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none',
  alertTypeLabel: 'w-[80px]',
  label: 'w-[150px]',
  alertContactInput: 'w-1/4',
  input: 'w-16',
  icon: 'pl-3',
}
